/**
 * useUpgradeGuard Hook (Story 2.9, Task 2)
 *
 * One-call enforcement hook that wraps useFeatureGate + UpgradeLimitModal state.
 * Call `guardAction(yourAction)` — it either executes the action (if allowed)
 * or shows the upgrade modal (if blocked).
 *
 * Returns `{ guardAction, UpgradeModal, isPending }`.
 * The consumer MUST render `UpgradeModal` somewhere in the JSX tree.
 */

import { useState, useCallback, useRef, useEffect, createElement, type ReactElement } from "react";
import { useFeatureGate } from "./useFeatureGate";
import { usePlanLimits } from "./usePlanLimits";
import { UpgradeLimitModal } from "../components/billing/UpgradeLimitModal";
import { PLAN_LIMITS } from "@provenleap/types";
import type { PlanFeature } from "@provenleap/types";

export interface UseUpgradeGuardOptions {
  feature: PlanFeature;
  currentCount?: number;
  billingInterval?: "monthly" | "annual";
  onError?: (error: unknown) => void;
  /**
   * Execute even when the local count gate is closed so a rollout-aware server
   * endpoint can make the authoritative admission decision.
   */
  serverAuthoritative?: boolean;
}

export interface UseUpgradeGuardResult {
  /** Wraps an action — executes it if allowed, shows upgrade modal if blocked. */
  guardAction: (action: () => void | Promise<void>) => Promise<void>;
  /** JSX element to render — the upgrade modal. MUST be placed in the component tree. */
  UpgradeModal: ReactElement;
  /** True while the guarded action is executing. */
  isPending: boolean;
  /** Open the existing upgrade modal after an authoritative server rejection. */
  showUpgradeModal: () => void;
}

export function useUpgradeGuard({
  feature,
  currentCount,
  billingInterval = "monthly",
  onError,
  serverAuthoritative = false,
}: UseUpgradeGuardOptions): UseUpgradeGuardResult {
  const gate = useFeatureGate(feature, currentCount);
  const { plan } = usePlanLimits();

  const [modalOpen, setModalOpen] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const pendingRef = useRef(false);

  // Cleanup on unmount: signal abort so post-action state updates are suppressed.
  // NOTE: The AbortController does NOT cancel the user's action (the action does
  // not receive the signal). It only prevents setState calls after unmount.
  useEffect(() => {
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, []);

  const guardAction = useCallback(
    async (action: () => void | Promise<void>) => {
      // Debounce: if already executing, no-op
      if (pendingRef.current) return;

      // If count is unknown (checked === false), don't allow action
      if (!gate.checked) {
        if (process.env.NODE_ENV === "development") {
          console.warn(
            `[useUpgradeGuard] Feature "${feature}" count is unknown (checked=false). ` +
              `Cannot proceed — provide currentCount to useUpgradeGuard.`
          );
        }
        return;
      }

      // If blocked, show modal
      if (!gate.allowed && !serverAuthoritative) {
        setModalOpen(true);
        return;
      }

      // Allowed — execute the action
      pendingRef.current = true;
      setIsPending(true);

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const result = action();
        if (result instanceof Promise) {
          await result;
        }
      } catch (error) {
        if (controller.signal.aborted) return; // Component unmounted
        if (onError) {
          onError(error);
        } else {
          throw error;
        }
      } finally {
        if (!controller.signal.aborted) {
          pendingRef.current = false;
          setIsPending(false);
        }
        abortRef.current = null;
      }
    },
    [gate.allowed, gate.checked, feature, onError, serverAuthoritative]
  );

  const showUpgradeModal = useCallback(() => setModalOpen(true), []);

  // Compute nextTierLimit from PLAN_LIMITS
  const upgradePlan = gate.upgradePlan;
  const nextTierLimit = upgradePlan
    ? (() => {
        const upgradeLimit = PLAN_LIMITS[upgradePlan][feature];
        return typeof upgradeLimit === "number" ? upgradeLimit : null;
      })()
    : null;

  const modal = createElement(UpgradeLimitModal, {
    open: modalOpen,
    onOpenChange: setModalOpen,
    feature,
    currentPlan: plan,
    currentUsage: currentCount ?? 0,
    currentLimit: typeof gate.limit === "number" ? gate.limit : 0,
    upgradePlan,
    nextTierLimit,
    billingInterval,
  });

  return { guardAction, UpgradeModal: modal, isPending, showUpgradeModal };
}
